/**
 * Kronos-domain mutations — China B-mono P2 seam, Task 4.
 *
 * Converges every inline `timeEntries` write from `TimeTracker.tsx`
 * (manual entries, timer entries, inline recap edits, duplicate) and
 * `CalendarView.tsx` (create/paste, drag-move, drag-resize, popover
 * save, delete) behind this module, mirroring `adminMutations.ts` /
 * `projectMutations.ts` (Tasks 2-3). Every function is a verbatim move
 * of the inline write it replaces — same collection path, same payload
 * fields, same timestamp calls. See
 * docs/kb/architecture/data-mutation-module-pattern.md.
 *
 * `src/features/kronos/timeEntries.ts` — the file the Task 4 brief
 * expected to hold "existing time-entry write logic" — turns out to
 * contain only pure, dependency-free helpers (parsing, formatting,
 * payload *shaping*); it has zero Firestore calls to re-export (same
 * class of finding as Task 3's DeleteProjectDialog/DesignationDetailPanel
 * check — the brief's file list is a hypothesis, not a guarantee).
 * `duplicateTimeEntry` below still reuses its `buildDuplicatePayload`
 * helper rather than re-deriving the copy shape.
 *
 * Two pairs of call-sites write byte-for-byte identical payloads and are
 * intentionally collapsed into one function each (same precedent as
 * `deleteDesignation` in `projectMutations.ts`):
 *   - `createTimerTimeEntry`      — TimeTracker's handleContinueTimer + handleTimerSave
 *   - `createCalendarTimeEntry`   — CalendarView's pasteAt + handleSave (create branch)
 *   - `updateTimeEntryTiming`     — CalendarView's handleDragMove + handleDragResize
 *   - `deleteTimeEntry`           — TimeTracker's handleDelete + CalendarView's handleDelete
 *
 * Note the two domains' create/update payloads are NOT interchangeable:
 * TimeTracker writes stamp `createdAt`/`updatedAt` via `serverTimestamp()`;
 * CalendarView writes stamp `createdAt` via `Timestamp.now()` and never
 * write `updatedAt` at all. Both shapes are preserved verbatim rather than
 * unified.
 */
import { Timestamp } from 'firebase/firestore';
import type { TimeEntry } from '@altyor-kronos/shared-types';
import { buildDuplicatePayload } from '../../features/kronos/timeEntries';
import { apiPost, apiPut, apiDelete } from './restClient';

// Under REST the server stamps createdAt/updatedAt, so the client omits them;
// startTime/endTime stay Firestore Timestamps — apiPost JSON-encodes them to the
// {type:'firestore/timestamp/1.0',…} shape the server revives. Date fields in a
// recap patch are normalised to Timestamps the same way the SDK path does.
const toTs = (v: unknown) => (v instanceof Date ? Timestamp.fromDate(v) : v);

// --- TimeTracker.tsx ---

export interface CreateManualTimeEntryInput {
  userId: string;
  projectId: string;
  designationId: string;
  description: string;
  tags: string[];
  startTime: Date;
  endTime: Date;
  durationSeconds: number;
  billable: boolean;
}

/** Verbatim move of `TimeTracker.handleCreate`'s write. */
export async function createManualTimeEntry(input: CreateManualTimeEntryInput): Promise<void> {
  const payload = {
    userId: input.userId,
    projectId: input.projectId,
    designationId: input.designationId,
    description: input.description.trim() || null,
    tags: input.tags,
    startTime: Timestamp.fromDate(input.startTime),
    endTime: Timestamp.fromDate(input.endTime),
    durationSeconds: input.durationSeconds,
    billable: input.billable,
    source: 'manual',
  };
  await apiPost('/timeEntries', payload);
}

export interface CreateTimerTimeEntryInput {
  userId: string;
  projectId: string;
  designationId: string;
  description: string | null;
  tags: string[];
  startTime: Date;
  endTime: Date;
  durationSeconds: number;
  billable: boolean;
}

/**
 * Shared by `TimeTracker.handleContinueTimer` and `handleTimerSave` —
 * identical write shape in both (source: 'timer', both timestamps via
 * `serverTimestamp()`); only the field values differ at the call site.
 */
export async function createTimerTimeEntry(input: CreateTimerTimeEntryInput): Promise<void> {
  const payload = {
    userId: input.userId,
    projectId: input.projectId,
    designationId: input.designationId,
    description: input.description,
    tags: input.tags,
    startTime: Timestamp.fromDate(input.startTime),
    endTime: Timestamp.fromDate(input.endTime),
    durationSeconds: input.durationSeconds,
    billable: input.billable,
    source: 'timer',
  };
  await apiPost('/timeEntries', payload);
}

/**
 * Verbatim move of `TimeTracker.handleInlineUpdate`'s write, including the
 * Date -> Timestamp normalisation for `startTime`/`endTime` recap-edit
 * fields. The "not authenticated" guard stays at the call site — it is an
 * authorization check, not part of the write shape.
 */
export async function updateTimeEntryFields(
  entryId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  // Server stamps updatedAt; normalise any Date fields to Timestamps.
  await apiPut(`/timeEntries/${entryId}`, {
    ...patch,
    ...(patch.startTime !== undefined ? { startTime: toTs(patch.startTime) } : {}),
    ...(patch.endTime !== undefined ? { endTime: toTs(patch.endTime) } : {}),
  });
}

/**
 * Verbatim move of `TimeTracker.handleDuplicate`'s write. Reuses
 * `buildDuplicatePayload` from `timeEntries.ts` (the sanctioned home for
 * that payload-shaping logic) rather than re-deriving the copy's fields.
 */
export async function duplicateTimeEntry(entry: TimeEntry): Promise<void> {
  const payload = buildDuplicatePayload(entry);
  await apiPost('/timeEntries', payload);
}

/**
 * Shared by `TimeTracker.handleDelete` and `CalendarView.handleDelete` —
 * both are the exact same `deleteDoc(doc(db, 'timeEntries', id))` call.
 */
export async function deleteTimeEntry(entryId: string): Promise<void> {
  await apiDelete(`/timeEntries/${entryId}`);
}

// --- CalendarView.tsx ---

export interface CreateCalendarTimeEntryInput {
  userId: string;
  projectId: string;
  designationId: string;
  description: string;
  startTime: Date;
  endTime: Date;
  durationSeconds: number;
  tags: string[];
  billable: boolean;
}

/**
 * Shared by `CalendarView.pasteAt` and the create branch of `handleSave` —
 * identical write shape in both (source: 'manual', `createdAt` stamped via
 * `Timestamp.now()`, no `updatedAt` — unlike TimeTracker's manual create,
 * this one is preserved verbatim and NOT unified with it).
 */
export async function createCalendarTimeEntry(input: CreateCalendarTimeEntryInput): Promise<void> {
  const payload = {
    userId: input.userId,
    projectId: input.projectId,
    designationId: input.designationId,
    description: input.description,
    startTime: Timestamp.fromDate(input.startTime),
    endTime: Timestamp.fromDate(input.endTime),
    durationSeconds: input.durationSeconds,
    tags: input.tags,
    billable: input.billable,
    source: 'manual',
  };
  await apiPost('/timeEntries', payload);
}

export interface UpdateCalendarTimeEntryInput {
  projectId: string;
  designationId: string;
  description: string;
  startTime: Date;
  endTime: Date;
  durationSeconds: number;
  tags: string[];
  billable: boolean;
}

/** Verbatim move of the update branch of `CalendarView.handleSave`. */
export async function updateCalendarTimeEntry(
  entryId: string,
  input: UpdateCalendarTimeEntryInput,
): Promise<void> {
  const payload = {
    projectId: input.projectId,
    designationId: input.designationId,
    description: input.description,
    startTime: Timestamp.fromDate(input.startTime),
    endTime: Timestamp.fromDate(input.endTime),
    durationSeconds: input.durationSeconds,
    tags: input.tags,
    billable: input.billable,
  };
  await apiPut(`/timeEntries/${entryId}`, payload);
}

export interface UpdateTimeEntryTimingInput {
  startTime: Date;
  endTime: Date;
  durationSeconds: number;
}

/**
 * Shared by `CalendarView.handleDragMove` and `handleDragResize` —
 * identical write shape in both.
 */
export async function updateTimeEntryTiming(
  entryId: string,
  input: UpdateTimeEntryTimingInput,
): Promise<void> {
  const payload = {
    startTime: Timestamp.fromDate(input.startTime),
    endTime: Timestamp.fromDate(input.endTime),
    durationSeconds: input.durationSeconds,
  };
  await apiPut(`/timeEntries/${entryId}`, payload);
}
